import { Box, Grid, Stack } from '@mui/material';
import DashboardCard from '../components/DashboardCard';
import ColumnChart from '../components/ColumnChart';
import DashboardDatagrid from '../components/DashboardDatagrid';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import DashboardToolbar from '@/features/dashboards/components/DashboardToolbar';
import DashboardDateRangeSelector from '@/features/dashboards/components/DashboardDateRangeSelector';
import DashboardUnitSelector from '@/features/dashboards/components/DashboardUnitSelector';
import FilterDialog from '@/features/dashboards/components/FilterDialog';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';
import AggregationSelector from '@/features/dashboards/components/AggregationSelector';
import Kpi from '../components/Kpi';
import { useGetBasicEnergyDataQuery } from '@/api/energy-analysis/basicEnergySlice';
import useSearchParams from '@/hooks/useStableSearchParams';
import { useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { formatNumber } from '@/utils/formatters';
import LatestRefreshFooter from '@/features/dashboards/components/LatestRefreshFooter';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import { useTranslation } from 'react-i18next';

const WaterBasic = () => {
  const { t } = useTranslation('dashboards-water-basic');
  const { accountId } = useParams();
  const [searchParams] = useSearchParams();

  const startTimestamp = searchParams.get('from');
  const endTimestamp = searchParams.get('to');
  const agg = searchParams.get('agg') || 'day';
  const unitIds = searchParams.get('units');

  const [isUpdating, setIsUpdating] = useState(false);
  const handleRefresh = () => {
    // Use translation for the placeholder message
    // If your environment uses plain strings for alerts, you can wrap this in a nicer UI later
    alert(t('messages.implementationComingSoon'));
  };

  const { data: BasicEnergyData, isFetching } = useGetBasicEnergyDataQuery(
    {
      analysisType: 'water',
      accountId,
      startTimestamp,
      endTimestamp,
      aggregateFrequency: agg,
      unitIds: unitIds ? unitIds.split(',') : [],
    },
    {
      skip: !accountId || !startTimestamp || !endTimestamp,
      pollingInterval: isUpdating ? 15000 : 0,
    },
  );

  useEffect(() => {
    if (BasicEnergyData?.Metadata?.isUpdating) {
      setIsUpdating(true);
    } else {
      setIsUpdating(false);
    }
  }, [BasicEnergyData, setIsUpdating]);

  // table columns rely on translations, so define them inside the component
  const tableColumns = [
    { field: 'name', headerName: t('table.columns.name'), flex: 1 },
    {
      field: 'sum',
      headerName: t('table.columns.sum'),
      type: 'number',
      flex: 0.6,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: 'maxh',
      headerName: t('table.columns.maxh'),
      type: 'number',
      flex: 0.4,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value) => formatNumber(value),
    },
  ];

  const chartData = useMemo(() => {
    if (!BasicEnergyData || !BasicEnergyData?.AggregatedData)
      return { categories: [], series: [] };
    const sorted = [...BasicEnergyData.AggregatedData].sort(
      (a, b) =>
        DateTime.fromISO(a.timestamp).toMillis() -
        DateTime.fromISO(b.timestamp).toMillis(),
    );
    const series = [
      {
        name: t('chart.series.consumption'),
        data: sorted.map((d) => [
          DateTime.fromISO(d.timestamp).toMillis(),
          d.sum,
        ]),
      },
    ];
    return { categories: [], series };
  }, [BasicEnergyData, t]);

  const tableData = useMemo(() => {
    const rows = BasicEnergyData?.MeterSummaries?.map((ms) => ({
      id: ms.UnitId,
      name: ms.UnitName,
      sum: ms.TotalValue,
      maxh: ms.MaxValue,
    }));
    return rows || [];
  }, [BasicEnergyData]);

  const totalConsumption = useMemo(() => {
    if (!BasicEnergyData || !BasicEnergyData?.MeterSummaries) return 0;
    return BasicEnergyData.MeterSummaries.reduce(
      (acc, ms) => acc + (ms.TotalValue || 0),
      0,
    );
  }, [BasicEnergyData]);

  return (
    <Box>
      <DashboardToolbar
        title={t('title')}
        rightSlot={
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              gap: 2,
              flexDirection: 'row',
            }}
          >
            <FilterDialog>
              <DashboardUnitSelector />
            </FilterDialog>
            <DashboardDateRangeSelector />
          </Box>
        }
      />
      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={4}>
          <Stack gap={2} height="100%">
            <DashboardCard
              title={t('cards.totalConsumption')}
              icon={<SpeedRoundedIcon />}
            >
              <Kpi
                label={t('kpi.label')}
                value={formatNumber(totalConsumption)}
                unit={t('kpi.unit')}
              />
            </DashboardCard>
            <DashboardCard
              title={t('cards.powerUnits')}
              icon={<SensorsRoundedIcon />}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
              }}
            >
              <DashboardDatagrid rows={tableData} columns={tableColumns} />
            </DashboardCard>
          </Stack>
        </Grid>

        <Grid item xs={12} md={8}>
          <DashboardCard
            icon={<BarChartRoundedIcon />}
            title={t('cards.consumptionByDay')}
            actions={<AggregationSelector />}
          >
            <ColumnChart
              data={chartData}
              unit={t('kpi.unit')}
              height={600}
              isLoading={isFetching}
            />
          </DashboardCard>
        </Grid>
      </Grid>
      <LatestRefreshFooter
        latestRefresh={DateTime.now().toISO()}
        isUpdating={isUpdating}
        onRefresh={handleRefresh}
      />
    </Box>
  );
};

export default WaterBasic;
